import { useContext, useEffect, useRef } from "react";
import AuthContext from "../context/authContext/AuthContext";
import useLogoutAllTabs from "../hooks/logoutAllTabs";

const REACT_APP_PORTAL_URL = process.env[`REACT_APP_PORTAL_URL`];

const AdminPortalWrapper = () => {
    const { state: authState } = useContext(AuthContext);
    const ref = useRef<HTMLIFrameElement | null>(null);
    const { logoutAllTabs, postMessage } = useLogoutAllTabs();

    const sendMessage = () => {
        if (!ref.current) {
            return;
        }

        ref.current?.contentWindow?.postMessage("AHA_WEB_logout", "*");
        window.location.href = `${REACT_APP_PORTAL_URL}/auth/login`;
    };

    // Named so the add and the remove are the same reference; this is a routed screen, so a leaked
    // listener would add one per visit.
    // Only the framed admin app may log us out. REACT_APP_PORTAL_URL also builds the iframe src, so
    // if it were missing the screen would break visibly rather than the guard failing quietly.
    const onAdminLogoutMessage = (event: MessageEvent) => {
        if (
            event.origin === REACT_APP_PORTAL_URL &&
            event.data === "AHA_ADMIN_LOGOUT"
        ) {
            sendMessage();
            postMessage("logout");
            window.location.href = `${REACT_APP_PORTAL_URL}/auth/login`;
        }
    };

    useEffect(() => {
        window.addEventListener("message", onAdminLogoutMessage);

        logoutAllTabs(sendMessage);

        return () => {
            window.removeEventListener("message", onAdminLogoutMessage);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logoutAllTabs, postMessage]);

    useEffect(() => {
        const iframe = ref.current;
        const handleLoad = () => {
            if (iframe?.contentWindow) {
                iframe.contentWindow.postMessage(
                    {
                        type: "AHA_WEB_LOGIN",
                        token: authState.token.accessToken,
                    },
                    REACT_APP_PORTAL_URL
                );
            }
        };
        iframe?.addEventListener("load", handleLoad);
        return () => {
            iframe?.removeEventListener("load", handleLoad);
        };
    }, [authState.token]);

    return (
        <iframe
            ref={ref}
            style={{
                position: "absolute",
                top: 0,
                height: "100vh",
                width: "100%",
                background: "white",
            }}
            title="AHA Admin Portal"
            src={`${REACT_APP_PORTAL_URL}/auth/login-from-web`}
        ></iframe>
    );
};

export default AdminPortalWrapper;
